import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import CreateShop from './pages/CreateShop';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import AddProduct from './pages/AddProduct';
import Orders from './pages/Orders';
import Offers from './pages/Offers';
import Conversations from './pages/Conversations';
import Stats from './pages/Stats';
import Payments from './pages/Payments';
import Profile from './pages/Profile';
import './i18n/config';
import { createSellerQueryClient } from './lib/queryClient';

const queryClient = createSellerQueryClient();

const AppRoutes: React.FC = () => {
  const { token, seller, initializing } = useAuth();

  if (initializing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-primary"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={token ? <Navigate to={seller ? '/' : '/create-shop'} replace /> : <Login />}
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/signup" element={<Navigate to="/login" replace />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* Create shop: logged in, no seller yet. Guard: block if shop already exists */}
      <Route
        path="/create-shop"
        element={
          <ProtectedRoute>
            {seller ? <Navigate to="/" replace /> : <CreateShop />}
          </ProtectedRoute>
        }
      />

      {/* Dashboard and seller pages: require login + seller (shop exists). Guard: block if no shop */}
      <Route
        path="/"
        element={
          <ProtectedRoute requireSeller>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute requireSeller>
            <Layout>
              <Products />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/add"
        element={
          <ProtectedRoute requireSeller>
            <Layout>
              <AddProduct />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/edit/:id"
        element={
          <ProtectedRoute requireSeller>
            <Layout>
              <AddProduct />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/categories" element={<Navigate to="/products" replace />} />
      <Route path="/brands" element={<Navigate to="/products" replace />} />
      <Route path="/attributes" element={<Navigate to="/products" replace />} />
      <Route path="/attributes/:attributeId/values" element={<Navigate to="/products" replace />} />
      <Route
        path="/orders"
        element={
          <ProtectedRoute requireSeller>
            <Layout>
              <Orders />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/offers"
        element={
          <ProtectedRoute requireSeller>
            <Layout>
              <Offers />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/bids" element={<Navigate to="/offers" replace />} />
      <Route
        path="/conversations"
        element={
          <ProtectedRoute requireSeller>
            <Layout>
              <Conversations />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stats"
        element={
          <ProtectedRoute requireSeller>
            <Layout>
              <Stats />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute requireSeller>
            <Layout>
              <Payments />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/earnings" element={<Navigate to="/payments" replace />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute requireSeller>
            <Layout>
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
